import React, { useEffect, useState } from "react";
import axios from "axios";
import { Authorization } from "../../Context/Authorization";
import Preloader from "../../shared/PreLoaders";
import "../../dashboard/reporttable.css";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
const inputStyle = { width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0", borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff", };

const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "flex", alignItems: "center", gap: 5, };
const TogglePill = ({
    label,
    icon,
    checked,
    onChange,
    disabled = false,
}) => (
    <label
        style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 16px",
            borderRadius: 999,
            border: "1px solid #2E7D32",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.5 : 1,
            background: checked ? "#E8F5E9" : "#fff",
        }}
    >
        <input
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={onChange}
            style={{ display: "none" }}
        />

        <i className={`mdi ${icon}`} />
        {label}
    </label>
);

const MeetingReview = () => {
    const Api = process.env.REACT_APP_API;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    /* Master Data */
    const [circles, setCircles] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const [jfmcTypes, setJfmcTypes] = useState([]);
    /* Report */
    const [meetingData, setMeetingData] = useState([]);
    /* Filters */
    const today = new Date();
    const [selectedMonth, setSelectedMonth] = useState(
        `${today.getFullYear()}-${String(today.getMonth()).padStart(2, "0")}`
    );
    const [year, month] = selectedMonth.split("-");
    const [selectedCircle, setSelectedCircle] = useState("");
    const [selectedDivision, setSelectedDivision] = useState("");
    const [selectedRange, setSelectedRange] = useState("");
    const [selectedJfmcType, setSelectedJfmcType] = useState("");
    const [selectedMeetingType, setSelectedMeetingType] =
        useState("EC Meetings");
    const [showCircle, setShowCircle] = useState(false);
    const [showDivision, setShowDivision] = useState(false);
    const [selectedLevel, setSelectedLevel] = useState("circle");
    const [expandedDivisions, setExpandedDivisions] = useState({});
    /* -------------------------------------- */
    const fetchMasterData = async () => {
        try {
            const config = Authorization();
            const [circleRes, divisionRes, jfmcRes] = await Promise.all([
                axios.get(`${Api}/api/circles`, config),
                axios.get(`${Api}/api/divisions`, config),
                axios.get(`${Api}/api/jfmc-types`, config),
            ]);
            setCircles(circleRes.data || []);
            setDivisions(divisionRes.data || []);
            const jfmcTypeList = jfmcRes.data || [];
            setJfmcTypes(jfmcTypeList);
            // Automatically select VFC JFMC Type
            if (!selectedJfmcType) {
                const defaultJfmcType = jfmcTypeList.find(
                    item => item.jfmcTypeName === "VFC"
                );

                if (defaultJfmcType) {
                    setSelectedJfmcType(defaultJfmcType._id);
                }
            }
        } catch (err) {
            console.error(err);
            setError("Failed to load filters.");
        }
    };
    /* -------------------------------------- */
    const fetchMeetingReview = async (jfmcTypeId = selectedJfmcType) => {
        
        try {
            setLoading(true);
            const config = Authorization();
            const res = await axios.get(
                `${Api}/api/vfc/review/meetings`,
                {
                    ...config,
                    params: {
                        month,
                        year,
                        circleId: selectedCircle || undefined,
                        divisionId: selectedDivision || undefined,
                        rangeId: selectedRange || undefined,
                        jfmcTypeId,
                        meetingType: selectedMeetingType,
                    },
                }
            );
            setMeetingData(res.data.data || []);
        } catch (error) {
            console.error("Bank balance Review Error:", error);
            setMeetingData([])
            // Optional: show a toast or alert
            toast.error(
                error.response?.data?.message || "Failed to load Meetings review data."
            );
        } finally {
            setLoading(false);
        }
    };
    const formatLastUpdated = (date) => {
        if (!date) return "-";

        return new Date(date).toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };
    /* -------------------------------------- */
    useEffect(() => {
        fetchMasterData();
    }, []);
    /* -------------------------------------- */
    useEffect(() => {
        if (!selectedJfmcType) return;
        fetchMeetingReview();
    }, [selectedMonth, selectedCircle, selectedDivision, selectedRange, selectedJfmcType, selectedMeetingType,]);
    const activeJfmc = jfmcTypes.find(
        (item) => item._id === selectedJfmcType
    );
    /* -------------------------------------- */
    const badge = (value, type) => {
        const styles = {
            total: {
                bg: "#EEF2FF",
                color: "#3730A3",
            },
            held: {
                bg: "#DCFCE7",
                color: "#166534",
            },
            pending: {
                bg: "#FEE2E2",
                color: "#991B1B",
            },
        };
        const style = styles[type];
        return (
            <span
                style={{ display: "inline-block", minWidth: 40, textAlign: "center", padding: "4px 10px", borderRadius: 20, background: style.bg, color: style.color, fontWeight: 600, fontSize: 12, }}>
                {value}
            </span>
        );
    };
    /* ---------------- Summary ---------------- */
    const totals = meetingData.reduce(
        (acc, item) => {
            acc.totalVfcs += item.totalVfcs || 0;
            acc.totalRanges += item.totalRanges || 0;
            acc.totalDivisions += item.totalDivisions || 0;
            acc.held += item.held || 0;
            acc.notHeld += item.notHeld || 0;

            return acc;
        },
        {
            totalVfcs: 0,
            totalRanges: 0,
            totalDivisions: 0,
            held: 0,
            notHeld: 0,
        }
    );
    const toggleDivision = (id) => {
        setExpandedDivisions(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };
    /* -------------------------------------- */
    const exportMeetingReport = () => {
        if (!meetingData || meetingData.length === 0) {
            alert("No data available to export");
            return;
        }

        // =====================================================
        // JFMC TYPE HEADINGS
        // =====================================================

        const selectedType = jfmcTypes.find(
            (type) =>
                String(type._id) === String(selectedJfmcType)
        );

        const jfmcTypeName =
            selectedType?.jfmcTypeName || "JFMC";

        const totalJfmcHeading =
            `Total JFMC (${jfmcTypeName})`;

        const completedJfmcHeading =
            `Completed JFMC (${jfmcTypeName})`;

        const notCompletedJfmcHeading =
            `Not Completed JFMC (${jfmcTypeName})`;
        const meetingTypeName =
            selectedMeetingType === "EC Meetings"
                ? "EC Meetings"
                : selectedMeetingType === "General Body"
                    ? "General Body"
                    : "Meeting";
        // =====================================================
        // GET MONTH + YEAR
        // =====================================================

        let reportYear;
        let reportMonth;

        /*
         * Handles both:
         *
         * month = "2026-07"
         *
         * OR
         *
         * month = "07"
         * year = "2026"
         */

        if (
            typeof month === "string" &&
            /^\d{4}-\d{2}$/.test(month)
        ) {
            const [y, m] = month.split("-");

            reportYear = Number(y);
            reportMonth = Number(m);
        } else {
            reportYear = Number(year);
            reportMonth = Number(month);
        }

        // =====================================================
        // MONTH NAME
        // =====================================================

        const monthDate = new Date(
            reportYear,
            reportMonth - 1,
            1
        );

        const monthName = monthDate.toLocaleString(
            "en-US",
            {
                month: "long"
            }
        );

        // =====================================================
        // REPORT TITLE
        // =====================================================

        const reportTitle =
            `${meetingTypeName} Summary for the Month of ${monthName} ${reportYear}`;
        const excelData = [];
        if (selectedRange) {
            const totalVfc = meetingData.length;
            const completedVfc =
                meetingData.filter(
                    (vfc) =>
                        vfc.held === true ||
                        vfc.completed === 1 ||
                        vfc.completed === true
                ).length;
            const notCompletedVfc =
                totalVfc - completedVfc;
            const percentage =
                totalVfc > 0
                    ? (
                        (completedVfc / totalVfc) *
                        100
                    ).toFixed(2)
                    : "0.00";
            excelData.push({
                "VFC":
                    meetingData[0]?.vfcName ||
                    meetingData[0]?.name ||
                    "-",
                [totalJfmcHeading]:
                    totalVfc,
                [completedJfmcHeading]:
                    completedVfc,
                [notCompletedJfmcHeading]:
                    notCompletedVfc,
                "Completion %":
                    `${percentage}%`
            });
        }
        else if (selectedDivision) {
            meetingData.forEach((range) => {
                const total =
                    Number(range.totalVfcs || 0);
                const completed =
                    Number(range.held || 0);
                const notCompleted =
                    Number(
                        range.notHeld ??
                        (total - completed)
                    );
                const percentage =
                    total > 0
                        ? (
                            (completed / total) *
                            100
                        ).toFixed(2)
                        : "0.00";
                excelData.push({
                    "Range":
                        range.rangeName ||
                        range.range?.rangeName ||
                        range.name ||
                        "-",
                    [totalJfmcHeading]:
                        total,
                    [completedJfmcHeading]:
                        completed,
                    [notCompletedJfmcHeading]:
                        notCompleted,
                    "Completion %":
                        `${percentage}%`
                });
            });
        }
        else if (selectedCircle) {
            meetingData.forEach((division) => {
                const total =
                    Number(division.totalVfcs || 0);
                const completed =
                    Number(division.held || 0);
                const notCompleted =
                    Number(
                        division.notHeld ??
                        (total - completed)
                    );
                const percentage =
                    total > 0
                        ? (
                            (completed / total) *
                            100
                        ).toFixed(2)
                        : "0.00";
                excelData.push({
                    "Division":
                        division.divisionName ||
                        division.name ||
                        "-",
                    [totalJfmcHeading]:
                        total,
                    [completedJfmcHeading]:
                        completed,
                    [notCompletedJfmcHeading]:
                        notCompleted,
                    "Completion %":
                        `${percentage}%`
                });
            });
        }
        else {

            meetingData.forEach(circle => {

                const divisions = [
                    ...(circle.completedDivisions || []),
                    ...(circle.pendingDivisions || [])
                ];

                // remove duplicates because a division can exist in both arrays
                const uniqueDivisions = [
                    ...new Map(
                        divisions.map(d => [d._id, d])
                    ).values()
                ];

                uniqueDivisions.forEach(division => {

                    const total = Number(division.totalVfcs || 0);
                    const completed = Number(division.held || 0);
                    const notCompleted = Number(division.notHeld || 0);

                    const percentage =
                        total > 0
                            ? ((completed / total) * 100).toFixed(2)
                            : "0.00";

                    excelData.push({
                        "Circle": circle.circleName,
                        "Division": division.divisionName,
                        [totalJfmcHeading]: total,
                        [completedJfmcHeading]: completed,
                        [notCompletedJfmcHeading]: notCompleted,
                        "Completion %": `${percentage}%`
                    });

                });

            });

        }
        if (excelData.length === 0) {
            alert("No data available to export");
            return;
        }
        const headers =
            Object.keys(excelData[0]);
        const rows =
            excelData.map((row) =>
                headers.map(
                    (header) => row[header]
                )
            );
        const worksheet =
            XLSX.utils.aoa_to_sheet([
                [reportTitle],
                [],
                headers,
                ...rows
            ]);
        worksheet["!merges"] = [
            {
                s: {
                    r: 0,
                    c: 0
                },
                e: {
                    r: 0,
                    c: headers.length - 1
                }
            }
        ];
        worksheet["!cols"] = headers.map(
            (header) => ({
                wch:
                    Math.max(
                        header.length + 3,
                        18
                    )
            })
        );
        const workbook =
            XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            "Meeting Summary"
        );
        XLSX.writeFile(
            workbook,
            `Meeting_Summary_${monthName}_${reportYear}.xlsx`
        );
    };
    if (loading) return <Preloader loading />;
    if (error) return <p className="text-danger">{error}</p>;
    /* ── Render ── */
    return (
        <div>
            <div className="rd-page-header">
                <div className="rd-header-left">
                    <span className="page-title-icon">
                        <i className="mdi mdi-clipboard-check-multiple-outline" />
                    </span>
                    <div>
                        <h3 className="rd-page-title">
                            Meeting Review Dashboard
                        </h3>
                        <p className="rd-page-sub">
                            Month-wise EC & General Body Meeting Review
                        </p>
                    </div>
                </div>

            </div>

            {/* ---------------- Filters ---------------- */}

            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", padding: "20px 24px", marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <span style={{ width: 30, height: 30, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14 }}>
                        <i className="mdi mdi-filter-variant" />
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>Filter Report</span>
                    <span style={{ fontSize: 11, color: "#A0AEC0", marginLeft: 4 }}>Toggle filters and select a date to view records</span>
                </div>

                {/* Toggle pills */}
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                    <TogglePill label="Circle" icon="mdi-circle-outline" checked={showCircle} disabled={showDivision} onChange={(e) => { setShowCircle(e.target.checked); if (!e.target.checked) setSelectedCircle(""); }} />
                    <TogglePill label="Division" icon="mdi-domain" checked={showDivision} disabled={showCircle} onChange={(e) => { setShowDivision(e.target.checked); if (!e.target.checked) setSelectedDivision(""); }} />
                </div>
                {/* Conditional selects */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 12, marginBottom: 16 }}>
                    <div >
                        <label style={labelStyle}>
                            <i
                                className="mdi mdi-calendar-month"
                                style={{ color: "#43A047" }}
                            />
                            Month
                        </label>
                        <input
                            type="month"
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(e.target.value)}
                            style={inputStyle}
                        />
                    </div>
                    <div>
                        <label style={labelStyle}>
                            <i
                                className="mdi mdi-account-group"
                                style={{ color: "#43A047" }}
                            />
                            Meeting Type
                        </label>
                        <select
                            value={selectedMeetingType}
                            onChange={(e) => setSelectedMeetingType(e.target.value)}
                            style={inputStyle}
                        >
                            <option value="EC Meetings">
                                EC Meetings
                            </option>
                            <option value="General Body">
                                General Body
                            </option>
                        </select>
                    </div>
                    <div>
                        <label style={labelStyle}>
                            <i
                                className="mdi mdi-shape-outline"
                                style={{ color: "#43A047" }}
                            />
                            JFMC Type
                        </label>
                        <select
                            value={selectedJfmcType}
                            onChange={(e) =>
                                setSelectedJfmcType(e.target.value)
                            }
                            style={inputStyle}
                            onFocus={(e) =>
                                (e.target.style.borderColor = "#4CAF50")
                            }
                            onBlur={(e) =>
                                (e.target.style.borderColor = "#E2E8F0")
                            }
                        >
                            <option value="">— Select —</option>
                            {jfmcTypes.sort((a, b) => a.jfmcTypeName.localeCompare(b.jfmcTypeName)).map((j) => (
                                <option key={j._id} value={j._id}>
                                    {j.jfmcTypeName}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 12, marginBottom: 16 }}>{showCircle && (
                    <div>
                        <label style={labelStyle}><i className="mdi mdi-circle-outline" style={{ color: "#43A047" }} /> Circle</label>
                        <select value={selectedCircle} onChange={(e) => setSelectedCircle(e.target.value)} style={inputStyle} onFocus={(e) => e.target.style.borderColor = "#4CAF50"} onBlur={(e) => e.target.style.borderColor = "#E2E8F0"}>
                            <option value="">— Select —</option>
                            {circles.sort((a, b) => a.circleName.localeCompare(b.circleName)).map((c) => <option key={c._id} value={c._id}>{c.circleName}</option>)}
                        </select>
                    </div>
                )}
                    {showDivision && (
                        <div>
                            <label style={labelStyle}><i className="mdi mdi-domain" style={{ color: "#43A047" }} /> Division</label>
                            <select value={selectedDivision} onChange={(e) => setSelectedDivision(e.target.value)} style={inputStyle} onFocus={(e) => e.target.style.borderColor = "#4CAF50"} onBlur={(e) => e.target.style.borderColor = "#E2E8F0"}>
                                <option value="">— Select —</option>
                                {divisions.sort((a, b) => a.divisionName.localeCompare(b.divisionName)).map((d) => <option key={d._id} value={d._id}>{d.divisionName}</option>)}
                            </select>
                        </div>
                    )}
                </div>
            </div>
            <div className="dashboard-sections">
                <div className="jfmc-section">
                    <h3 className="jfmc-title">
                        {activeJfmc.jfmcTypeName} — Compliance Overview
                    </h3>
                    <div
                        key={activeJfmc.jfmcTypeId}
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
                            gap: 16,
                        }}
                    >
                        <div className="stat-card card-blue stat-centered">
                            <div className="stat-icon-big icon-blue">
                                <i
                                    className={
                                        selectedRange
                                            ? "fas fa-building"
                                            : selectedDivision
                                                ? "fas fa-map"
                                                : selectedCircle
                                                    ? "fas fa-layer-group"
                                                    : "fas fa-sitemap"
                                    }
                                />
                            </div>
                            <div className="stat-title">
                                {selectedRange
                                    ? "VFCs"
                                    : selectedDivision
                                        ? "Ranges"
                                        : "Divisions"}
                            </div>
                            <div
                                style={{
                                    fontSize: 34,
                                    fontWeight: 700,
                                    marginTop: 12,
                                }}
                            >
                                {selectedRange
                                    ? totals.totalVfcs
                                    : selectedDivision
                                        ? totals.totalRanges
                                        : totals.totalDivisions}
                            </div>
                        </div>
                        {/* Total VFC */}
                        <div className="stat-card card-purple stat-centered">
                            <div className="stat-icon-big icon-purple">
                                <i className="fas fa-users-cog" />
                            </div>
                            <div className="stat-title">
                                Total JFMC
                            </div>
                            <div
                                style={{
                                    fontSize: 34,
                                    fontWeight: 700,
                                    marginTop: 12,
                                }}
                            >
                                {totals.totalVfcs}
                            </div>
                        </div>
                        {/* Meetings Held */}
                        <div className="stat-card card-green stat-centered">
                            <div className="stat-icon-big icon-green">
                                <i className="fas fa-check-circle" />
                            </div>
                            <div className="stat-title">
                                Meetings Held
                            </div>
                            <div
                                style={{
                                    fontSize: 34,
                                    fontWeight: 700,
                                    marginTop: 12,
                                }}
                            >
                                {totals.held}
                            </div>
                        </div>
                        {/* Meetings Not Held */}
                        <div className="stat-card card-orange stat-centered">
                            <div className="stat-icon-big icon-orange">
                                <i className="fas fa-times-circle" />
                            </div>
                            <div className="stat-title">
                                Meetings Not Held
                            </div>
                            <div
                                style={{
                                    fontSize: 34,
                                    fontWeight: 700,
                                    marginTop: 12,
                                }}
                            >
                                {totals.notHeld}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {
                selectedLevel !== "circle" && (
                    <button
                        className="btn btn-outline-secondary mb-3"
                        onClick={() => {
                            if (selectedLevel === "vfc") {

                                setSelectedRange("");

                                setSelectedLevel("range");

                                return;
                            }
                            if (selectedLevel === "range") {

                                setSelectedDivision("");

                                setSelectedLevel("division");

                                return;
                            }
                            if (selectedLevel === "division") {

                                setSelectedCircle("");

                                setSelectedLevel("circle");

                                return;
                            }

                        }}
                    >
                        <i className="mdi mdi-arrow-left" />
                        Back
                    </button>
                )
            }
            {/* ---------------- Summary Table ---------------- */}
            <div
                style={{
                    marginTop: 24,
                    background: "#fff",
                    borderRadius: 12,
                    border: "1px solid #E2E8F0",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
                    overflow: "hidden",
                }}
            >
                {/* Card Header */}
                <div
                    style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        padding: "14px 20px",
                        borderBottom: "1px solid #EDF2F7",
                    }}
                >
                    {/* Icon */}
                    <span
                        style={{
                            width: 36,
                            height: 36,
                            background: "linear-gradient(135deg,#1B5E20,#43A047)",
                            borderRadius: 9,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontSize: 16,
                        }}
                    >
                        <i className="mdi mdi-table-large" />
                    </span>
                    {/* Title + Subtitle */}
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            marginLeft: 12,
                            flex: 1,
                        }}
                    >
                        <div
                            style={{
                                fontSize: 14,
                                fontWeight: 700,
                                color: "#1A202C",
                            }}
                        >
                            {selectedMeetingType} Summary
                        </div>
                        <div
                            style={{
                                fontSize: 11,
                                color: "#A0AEC0",
                            }}
                        >
                            {selectedRange
                                ? "VFC Wise"
                                : selectedDivision
                                    ? "Range Wise"
                                    : selectedCircle
                                        ? "Division Wise"
                                        : "Circle Wise"}
                        </div>
                    </div>
                    {/* Button at the end/right */}
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            marginLeft: "auto",
                        }}
                        className="jfmc-switch"
                    >
                        <button
                            onClick={exportMeetingReport}
                            className="btn btn-success"
                        >
                            Export Excel
                        </button>
                    </div>
                </div>
                {/* Table */}
                <div style={{ overflowX: "auto" }}>
                    <table
                        id="meeting-review-table"
                        style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            minWidth: "1200px",
                        }}
                    >
                        <thead>
                            <tr
                                style={{
                                    background: "#2E7D32",
                                    color: "#fff",
                                }}
                            >
                                <th style={{ padding: 12, textAlign: "left" }}>
                                    {selectedRange
                                        ? "VFC"
                                        : selectedDivision
                                            ? "Range"
                                            : selectedCircle
                                                ? "Division"
                                                : "Circle"}
                                </th>
                                {selectedRange ? (
                                    <>
                                        <th style={{ padding: 12, textAlign: "center" }}>
                                            Status
                                        </th>

                                        <th style={{ padding: 12, textAlign: "center" }}>
                                            Meeting Date
                                        </th>
                                    </>
                                ) : (
                                    <>
                                        <th style={{ padding: 12, textAlign: "center" }}>
                                            Total JFMC
                                        </th>

                                        <th style={{ padding: 12, textAlign: "center" }}>
                                            Held
                                        </th>

                                        <th style={{ padding: 12, textAlign: "center" }}>
                                            Not Held
                                        </th>

                                        <th style={{ padding: 12 }}>
                                            {selectedCircle
                                                ? "Range Progress"
                                                : selectedDivision
                                                    ? "VFC Progress"
                                                    : "Division Progress"}
                                        </th>
                                    </>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {meetingData.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={selectedRange ? 3 : 6}
                                        style={{
                                            textAlign: "center",
                                            padding: 30,
                                            color: "#999",
                                        }}
                                    >
                                        No Records Found
                                    </td>
                                </tr>
                            ) : (
                                meetingData.map((item, index) => (
                                    <tr
                                        key={item._id}
                                        style={{
                                            background:
                                                index % 2 === 0 ? "#fff" : "#F8FAFD",
                                        }}
                                    >
                                        {/* Name */}
                                        <td
                                            style={{
                                                padding: "12px 15px",
                                                fontWeight: 600,
                                                color: "#1565C0",
                                                cursor: "pointer",
                                            }}
                                        >
                                            <i
                                                className="mdi mdi-map-marker-outline"
                                                style={{ marginRight: 5 }}
                                            />
                                            {item.circleName}
                                            {item.lastUpdated && (
                                                <div
                                                    style={{
                                                        fontSize: 11,
                                                        color: "#6B7280",
                                                        marginTop: 4
                                                    }}
                                                >
                                                    Last Updated: {formatLastUpdated(item.lastUpdated)}
                                                </div>
                                            )}
                                        </td>

                                        {selectedRange ? (
                                            <>
                                                {/* Status */}
                                                <td
                                                    style={{
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            display: "inline-block",
                                                            padding: "5px 12px",
                                                            borderRadius: 20,
                                                            fontSize: 12,
                                                            fontWeight: 600,
                                                            background: item.held
                                                                ? "#DCFCE7"
                                                                : "#FEE2E2",
                                                            color: item.held
                                                                ? "#166534"
                                                                : "#991B1B",
                                                        }}
                                                    >
                                                        {item.held ? "Held" : "Not Held"}
                                                    </span>
                                                </td>

                                                {/* Meeting Date */}
                                                <td
                                                    style={{
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    {item.meetingDate ? (
                                                        new Date(
                                                            item.meetingDate
                                                        ).toLocaleDateString("en-GB")
                                                    ) : (
                                                        "-"
                                                    )}
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                {/* Total VFC */}
                                                <td
                                                    style={{
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    {badge(item.totalVfcs, "total")}
                                                </td>

                                                {/* Held */}
                                                <td
                                                    style={{
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    {badge(item.held, "held")}
                                                </td>

                                                {/* Not Held */}
                                                <td
                                                    style={{
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    {badge(item.notHeld, "pending")}
                                                </td>

                                                {/* Held Names */}
                                                <td style={{ padding: 12, minWidth: 450 }}>

                                                    {selectedCircle
                                                        ? (
                                                            <>
                                                                {(() => {

                                                                    const divisions = [
                                                                        ...(item.completedDivisions || []),
                                                                        ...(item.pendingDivisions || [])
                                                                    ].filter(
                                                                        (value, index, self) =>
                                                                            index === self.findIndex(v => v._id === value._id)
                                                                    );

                                                                    if (!divisions.length) {
                                                                        return <span style={{ color: "#999" }}>No Divisions</span>;
                                                                    }

                                                                    return divisions.map(division => {

                                                                        const completed = (item.completedDivisions || []).some(
                                                                            d => d._id === division._id
                                                                        );

                                                                        return (
                                                                            <div
                                                                                key={division._id}
                                                                                style={{
                                                                                    border: "1px solid #E5E7EB",
                                                                                    borderLeft: `5px solid ${completed ? "#16A34A" : "#DC2626"}`,
                                                                                    borderRadius: 8,
                                                                                    padding: 12,
                                                                                    marginBottom: 12,
                                                                                    background: "#fff"
                                                                                }}
                                                                            >

                                                                                <div
                                                                                    onClick={() => toggleDivision(division._id)}
                                                                                    style={{
                                                                                        display: "flex",
                                                                                        justifyContent: "space-between",
                                                                                        alignItems: "center",
                                                                                        padding: "12px 15px",
                                                                                        cursor: "pointer",
                                                                                        background: completed ? "#F8FAFC" : "#FEF2F2",
                                                                                        borderRadius: 8
                                                                                    }}
                                                                                >

                                                                                    <div>

                                                                                        <div
                                                                                            style={{
                                                                                                fontWeight: 700,
                                                                                                color: completed ? "#2563EB" : "#DC2626"
                                                                                            }}
                                                                                        >
                                                                                            {division.divisionName}
                                                                                        </div>

                                                                                        <div
                                                                                            style={{
                                                                                                fontSize: 12,
                                                                                                color: "#666",
                                                                                                marginTop: 4
                                                                                            }}
                                                                                        >
                                                                                            {division.held}/{division.totalVfcs} Meetings
                                                                                            {division.lastUpdated && (
                                                                                                <div
                                                                                                    style={{
                                                                                                        fontSize: 12,
                                                                                                        fontWeight: 500,
                                                                                                        color: "#1565C0",
                                                                                                        marginTop: 3
                                                                                                    }}
                                                                                                >
                                                                                                    Last Updated: {formatLastUpdated(division.lastUpdated)}
                                                                                                </div>
                                                                                            )}
                                                                                        </div>

                                                                                    </div>

                                                                                    <div
                                                                                        style={{
                                                                                            display: "flex",
                                                                                            alignItems: "center",
                                                                                            gap: 15
                                                                                        }}
                                                                                    >

                                                                                        <span
                                                                                            style={{
                                                                                                padding: "3px 10px",
                                                                                                borderRadius: 20,
                                                                                                background: completed ? "#DCFCE7" : "#FEE2E2",
                                                                                                color: completed ? "#166534" : "#991B1B",
                                                                                                fontSize: 12,
                                                                                                fontWeight: 600
                                                                                            }}
                                                                                        >
                                                                                            {completed ? "Meeting Conducted" : "Pending"}
                                                                                        </span>

                                                                                        <i
                                                                                            className={`mdi ${expandedDivisions[division._id]
                                                                                                ? "mdi-chevron-up"
                                                                                                : "mdi-chevron-down"
                                                                                                }`}
                                                                                            style={{
                                                                                                fontSize: 24,
                                                                                                color: "#2563EB"
                                                                                            }}
                                                                                        />

                                                                                    </div>

                                                                                </div>

                                                                                {completed ? (
                                                                                    expandedDivisions[division._id] ? (
                                                                                        <>
                                                                                            {division.completedRanges.map((range) => (

                                                                                                <div
                                                                                                    key={range._id}
                                                                                                    style={{
                                                                                                        marginTop: 10,
                                                                                                        marginLeft: 20,
                                                                                                        padding: 10,
                                                                                                        borderLeft: "3px solid #22C55E"
                                                                                                    }}
                                                                                                >

                                                                                                    <div
                                                                                                        style={{
                                                                                                            display: "flex",
                                                                                                            justifyContent: "space-between",
                                                                                                            fontWeight: 600,
                                                                                                            marginBottom: 6
                                                                                                        }}
                                                                                                    >

                                                                                                        <span>{range.rangeName}</span>

                                                                                                        <span>
                                                                                                            {range.held}/{range.totalVfcs}
                                                                                                        </span>
                                                                                                        {range.lastUpdated && (
                                                                                                            <div
                                                                                                                style={{
                                                                                                                    fontSize: 11,
                                                                                                                    color: "#6B7280",
                                                                                                                    marginTop: 2
                                                                                                                }}
                                                                                                            >
                                                                                                                Last Updated: {formatLastUpdated(range.lastUpdated)}
                                                                                                            </div>
                                                                                                        )}
                                                                                                    </div>

                                                                                                    {range.completedVfcs.map((vfc) => (

                                                                                                        <div
                                                                                                            key={vfc._id}
                                                                                                            style={{
                                                                                                                display: "flex",
                                                                                                                justifyContent: "space-between",
                                                                                                                marginLeft: 18,
                                                                                                                padding: "3px 0",
                                                                                                                fontSize: 12
                                                                                                            }}
                                                                                                        >

                                                                                                            <span>
                                                                                                                ✓ {vfc.vfcName}
                                                                                                            </span>
                                                                                                            {vfc.lastUpdated && (
                                                                                                                <div
                                                                                                                    style={{
                                                                                                                        fontSize: 11,
                                                                                                                        color: "#6B7280"
                                                                                                                    }}
                                                                                                                >
                                                                                                                    Updated :
                                                                                                                    {" "}
                                                                                                                    {formatLastUpdated(vfc.lastUpdated)}
                                                                                                                </div>
                                                                                                            )}
                                                                                                            <span>
                                                                                                                {formatLastUpdated(vfc.meetingDate)}
                                                                                                            </span>

                                                                                                        </div>

                                                                                                    ))}

                                                                                                </div>

                                                                                            ))}
                                                                                        </>
                                                                                    ) : null
                                                                                ) : (
                                                                                    <div
                                                                                        style={{
                                                                                            color: "#991B1B",
                                                                                            fontSize: 13
                                                                                        }}
                                                                                    >
                                                                                        No meetings conducted in this division.
                                                                                    </div>
                                                                                )}

                                                                            </div>
                                                                        );

                                                                    });

                                                                })()}
                                                            </>
                                                        )
                                                        : (
                                                            <>
                                                                {(() => {

                                                                    const divisions = [
                                                                        ...(item.completedDivisions || []),
                                                                        ...(item.pendingDivisions || [])
                                                                    ].filter(
                                                                        (value, index, self) =>
                                                                            index === self.findIndex(v => v._id === value._id)
                                                                    );

                                                                    if (!divisions.length) {
                                                                        return <span style={{ color: "#999" }}>No Divisions</span>;
                                                                    }

                                                                    return divisions.map(division => {

                                                                        const isCompleted = (item.completedDivisions || []).some(
                                                                            d => d._id === division._id
                                                                        );

                                                                        return (
                                                                            <div
                                                                                key={division._id}
                                                                                style={{
                                                                                    border: "1px solid #E5E7EB",
                                                                                    borderRadius: 8,
                                                                                    padding: 10,
                                                                                    marginBottom: 12,
                                                                                    background: isCompleted ? "#fff" : "#FEF2F2"
                                                                                }}
                                                                            >

                                                                                <div
                                                                                    onClick={() => toggleDivision(division._id)}
                                                                                    style={{
                                                                                        display: "flex",
                                                                                        justifyContent: "space-between",
                                                                                        alignItems: "center",
                                                                                        padding: "12px",
                                                                                        cursor: "pointer",
                                                                                        background: isCompleted ? "#F8FAFC" : "#FEF2F2",
                                                                                        borderRadius: 8
                                                                                    }}
                                                                                >
                                                                                    <div>
                                                                                        <div
                                                                                            style={{
                                                                                                fontWeight: 700,
                                                                                                color: isCompleted ? "#2563EB" : "#DC2626"
                                                                                            }}
                                                                                        >
                                                                                            Division : {division.divisionName}
                                                                                        </div>

                                                                                        <div
                                                                                            style={{
                                                                                                fontSize: 12,
                                                                                                color: "#666",
                                                                                                marginTop: 4
                                                                                            }}
                                                                                        >
                                                                                            {division.held}/{division.totalVfcs} Meetings
                                                                                        </div>
                                                                                        {division.lastUpdated && (
                                                                                            <div
                                                                                                style={{
                                                                                                    fontSize: 11,
                                                                                                    color: "#6B7280",
                                                                                                    marginTop: 3
                                                                                                }}
                                                                                            >
                                                                                                Last Updated: {formatLastUpdated(division.lastUpdated)}
                                                                                            </div>
                                                                                        )}
                                                                                    </div>

                                                                                    <div
                                                                                        style={{
                                                                                            display: "flex",
                                                                                            alignItems: "center",
                                                                                            gap: 10
                                                                                        }}
                                                                                    >
                                                                                        {!isCompleted && (
                                                                                            <span
                                                                                                style={{
                                                                                                    color: "#DC2626",
                                                                                                    fontWeight: 600,
                                                                                                    fontSize: 12
                                                                                                }}
                                                                                            >
                                                                                                No Meeting
                                                                                            </span>
                                                                                        )}

                                                                                        <i
                                                                                            className={`mdi ${expandedDivisions[division._id]
                                                                                                ? "mdi-chevron-up"
                                                                                                : "mdi-chevron-down"
                                                                                                }`}
                                                                                            style={{
                                                                                                fontSize: 22
                                                                                            }}
                                                                                        />
                                                                                    </div>
                                                                                </div>

                                                                                {expandedDivisions[division._id] && (
                                                                                    division.completedRanges?.length ? (
                                                                                        division.completedRanges.map(range => (
                                                                                            <div
                                                                                                key={range._id}
                                                                                                style={{
                                                                                                    marginLeft: 15,
                                                                                                    marginBottom: 8
                                                                                                }}
                                                                                            >
                                                                                                <div style={{ fontWeight: 600 }}>
                                                                                                    Range : {range.rangeName} ({range.held}/{range.totalVfcs})
                                                                                                    {range.lastUpdated && (
                                                                                                        <div
                                                                                                            style={{
                                                                                                                fontSize: 11,
                                                                                                                color: "#6B7280",
                                                                                                                marginBottom: 6
                                                                                                            }}
                                                                                                        >
                                                                                                            Last Updated :
                                                                                                            {" "}
                                                                                                            {formatLastUpdated(range.lastUpdated)}
                                                                                                        </div>
                                                                                                    )}
                                                                                                </div>
                                                                                                {range.completedVfcs.map(vfc => (
                                                                                                    <div
                                                                                                        key={vfc._id}
                                                                                                        style={{
                                                                                                            display: "flex",
                                                                                                            justifyContent: "space-between",
                                                                                                            marginLeft: 20,
                                                                                                            fontSize: 12
                                                                                                        }}
                                                                                                    >
                                                                                                        <span>✓ {vfc.vfcName}</span>

                                                                                                        <span>
                                                                                                            {new Date(vfc.meetingDate)
                                                                                                                .toLocaleDateString("en-GB")}
                                                                                                        </span>
                                                                                                    </div>
                                                                                                ))}

                                                                                            </div>
                                                                                        ))
                                                                                    ) : (
                                                                                        <div
                                                                                            style={{
                                                                                                color: "#999",
                                                                                                marginLeft: 10,
                                                                                                fontSize: 12,
                                                                                                padding: 10
                                                                                            }}
                                                                                        >
                                                                                            No meetings held in this division
                                                                                        </div>
                                                                                    )
                                                                                )}
                                                                            </div>
                                                                        );

                                                                    });

                                                                })()}
                                                            </>
                                                        )}

                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>


    );
};

export default MeetingReview;
